import { useEffect } from 'react';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUserRole } from './useUserRole';
import { toast } from 'sonner';

const QUERY_KEY = 'feedback-notifications-topbar';

export function useFeedbackNotifications() {
  const queryClient = useQueryClient();
  const { hasRole } = useUserRole();
  const isAdmin = hasRole('admin');

  const { data: openCount = 0 } = useQuery({
    queryKey: [QUERY_KEY, isAdmin],
    queryFn: async (): Promise<number> => {
      if (!isAdmin) return 0;

      const { count, error } = await supabase
        .from('bug_reports')
        .select('*', { count: 'exact', head: true })
        .eq('status', 'open');

      if (error) {
        console.error('[useFeedbackNotifications] Error:', error);
        return 0;
      }

      return count || 0;
    },
    enabled: isAdmin,
    staleTime: 2 * 60 * 1000,
  });

  const { mutate: markAllAsReviewed, isPending: isMarkingAsReviewed } = useMutation({
    mutationFn: async () => {
      const { data, error } = await supabase.rpc('mark_feedback_as_reviewed');

      if (error) {
        console.error('[useFeedbackNotifications] markAllAsReviewed error:', error);
        throw error;
      }
      console.log('[useFeedbackNotifications] marked as reviewed, count:', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
    onError: (error) => {
      console.error('[useFeedbackNotifications] mutation error:', error);
      toast.error('Erro ao marcar feedback como lido');
    },
  });

  // Realtime subscription
  useEffect(() => {
    if (!isAdmin) return;

    const channel = supabase
      .channel('feedback-notifications-topbar')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'bug_reports',
        },
        () => {
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [isAdmin, queryClient]);

  return { openCount, isAdmin, markAllAsReviewed, isMarkingAsReviewed };
}
